/**
 * masterThemes — thèmes d'apparence du Master Shell (chantier « thèmes à la
 * WordPress », 2026-07-12). PUR (testable) + application DOM isolée.
 *
 * Un thème = un jeu de variables CSS (accents) appliqué sur :root en inline
 * (style.setProperty) — il surcharge les valeurs d'index.css sans les modifier
 * et reste réversible (reset = removeProperty, retour au thème par défaut).
 * Persistance : localStorage (immédiate) + tenant_branding.theme_settings du
 * tenant `global` (migration 20260712200200) pour la reprise multi-postes.
 * Architecture extensible côté tenant plus tard : les thèmes ne codent que
 * des variables, aucun sélecteur spécifique au Master Shell.
 */

const MASTER_THEME_STORAGE_KEY = 'rectorium_master_theme';

/** Variables surchargées par les thèmes (toutes retirées au reset). */
const THEME_VARS = [
  '--color-primary', '--primary', '--accent', '--accent2',
  '--primary-hover', '--primary-bg', '--accent-bg',
  '--sidebar-active-bg', '--sidebar-active-color',
];

const palette = (primary, hover, soft, accent2, sidebarColor) => ({
  '--color-primary': primary,
  '--primary': primary,
  '--accent': primary,
  '--accent2': accent2,
  '--primary-hover': hover,
  '--primary-bg': soft,
  '--accent-bg': soft,
  '--sidebar-active-bg': soft,
  '--sidebar-active-color': sidebarColor,
});

/** Galerie des thèmes prédéfinis. `default` = valeurs d'index.css (aucune surcharge). */
export const MASTER_THEMES = [
  { id: 'default', preview: ['#6D28D9', '#8b5cf6', '#c4b5fd'], vars: null },
  { id: 'ocean', preview: ['#0369a1', '#0ea5e9', '#7dd3fc'], vars: palette('#0369a1', '#075985', 'rgba(3,105,161,0.14)', '#0ea5e9', '#7dd3fc') },
  { id: 'emerald', preview: ['#047857', '#10b981', '#6ee7b7'], vars: palette('#047857', '#065f46', 'rgba(4,120,87,0.14)', '#10b981', '#6ee7b7') },
  { id: 'sunset', preview: ['#c2410c', '#f97316', '#fdba74'], vars: palette('#c2410c', '#9a3412', 'rgba(194,65,12,0.14)', '#f97316', '#fdba74') },
  { id: 'rose', preview: ['#be123c', '#f43f5e', '#fda4af'], vars: palette('#be123c', '#9f1239', 'rgba(190,18,60,0.14)', '#f43f5e', '#fda4af') },
  { id: 'slate', preview: ['#334155', '#64748b', '#cbd5e1'], vars: palette('#334155', '#1e293b', 'rgba(51,65,85,0.14)', '#64748b', '#cbd5e1') },
];

const getMasterTheme = (id) => MASTER_THEMES.find((th) => th.id === id) || null;

/** Applique un thème sur :root. `default`/inconnu = retrait des surcharges. */
export function applyMasterTheme(id, { persist = true } = {}) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const theme = getMasterTheme(id);
  THEME_VARS.forEach((v) => root.style.removeProperty(v));
  if (theme?.vars) {
    Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));
  }
  if (persist) {
    try {
      if (theme && theme.id !== 'default') localStorage.setItem(MASTER_THEME_STORAGE_KEY, theme.id);
      else localStorage.removeItem(MASTER_THEME_STORAGE_KEY);
    } catch { /* stockage indisponible : thème de session seulement */ }
  }
}

export function getStoredMasterThemeId() {
  try {
    return localStorage.getItem(MASTER_THEME_STORAGE_KEY) || 'default';
  } catch {
    return 'default';
  }
}

/** Réapplique le thème persisté au démarrage de l'application (main.jsx). */
export function initStoredMasterTheme() {
  const id = getStoredMasterThemeId();
  if (id && id !== 'default') applyMasterTheme(id, { persist: false });
}
